import { create } from "zustand";
import type {
  ArtifactType,
  BuildEdit,
  CrfDefinition,
  VisitDefinition,
  StudyArm,
  EligibilityCriterion,
  StudyEndpoint,
  EtmfFolder,
  EtmfDocument,
  QueryRule,
  StudyBuild,
  StudyBuilderMessage,
  ActiveToolCall,
  StudyBuildFull,
  StudyDocument,
} from "@/api/protocol-types";

interface StudyBuilderState {
  // Conversation
  buildId: string | null;
  messages: StudyBuilderMessage[];

  // Streaming
  streamingText: string;
  activeToolCalls: ActiveToolCall[];
  isStreaming: boolean;
  abortController: AbortController | null;

  // Build data
  build: StudyBuild | null;
  crfs: CrfDefinition[];
  visits: VisitDefinition[];
  arms: StudyArm[];
  eligibilityCriteria: EligibilityCriterion[];
  endpoints: StudyEndpoint[];
  etmfFolders: EtmfFolder[];
  etmfDocuments: EtmfDocument[];
  queryRules: QueryRule[];
  studyDocuments: StudyDocument[];

  // eTMF filing dialog
  etmfFilingFile: File | null;
  etmfFilingSuggestion: { folderId: string; expectedDocName: string; confidence?: number; reasoning?: string } | null;

  // Inspector state
  currentRound: number;
  maxRounds: number;
  retryStatus: { attempt: number; maxRetries: number; delayMs: number } | null;
  tokenUsage: { inputTokens: number; outputTokens: number } | null;

  // Linked EDC study
  linkedStudyId: string | null;
  selectedSiteId: string | null;

  // UI state
  activeGroup: string;
  selectedArtifact: { type: ArtifactType; id: string } | null;
  expandedTreeNodes: Set<string>;
  drawerOpen: boolean;
  editHistory: BuildEdit[];
  highlightTarget: { type: string; id: string } | null;
  historyPanelOpen: boolean;
  comparisonPanelOpen: boolean;
  comparisonBuildId: string | null;

  // Actions — Conversation
  setBuildId: (id: string | null) => void;
  loadConversation: (buildId: string, messages: StudyBuilderMessage[]) => void;
  addUserMessage: (content: string) => void;
  startStreaming: (controller: AbortController) => void;
  appendStreamingText: (chunk: string) => void;
  addToolCall: (tool: string, input: Record<string, unknown>) => void;
  completeToolCall: (tool: string, summary: string) => void;
  commitAssistantMessage: (
    id: string,
    artifacts?: { type: string; id: string; name: string }[],
    edits?: BuildEdit[],
    question?: { text: string; options?: string[] },
  ) => void;
  handleStreamError: (error: string) => void;
  cancelStream: () => void;

  // Actions — Inspector
  setRoundInfo: (round: number, maxRounds: number) => void;
  setRetryStatus: (status: { attempt: number; maxRetries: number; delayMs: number } | null) => void;
  setTokenUsage: (inputTokens: number, outputTokens: number) => void;

  // Actions — Build data
  loadBuild: (data: StudyBuildFull) => void;
  selectArtifact: (type: ArtifactType, id: string) => void;
  toggleTreeNode: (nodeId: string) => void;
  setDrawerOpen: (open: boolean) => void;
  applyEdit: (edit: BuildEdit) => void;
  clearBuilder: () => void;

  // Actions — Incremental artifact updates (SSE streaming)
  addCrf: (crf: CrfDefinition) => void;
  addVisit: (visit: VisitDefinition) => void;
  addArm: (arm: StudyArm) => void;
  addEligibilityCriteria: (criteria: EligibilityCriterion[]) => void;
  addEndpoint: (endpoint: StudyEndpoint) => void;
  addEtmfFolder: (folder: EtmfFolder) => void;
  addEtmfDocument: (doc: EtmfDocument) => void;
  addQueryRule: (rule: QueryRule) => void;
  addStudyDocument: (doc: StudyDocument) => void;
  updateOverview: (partial: Partial<StudyBuild>) => void;

  // Actions — eTMF filing
  setEtmfFilingFile: (file: File | null) => void;
  setEtmfFilingSuggestion: (s: { folderId: string; expectedDocName: string; confidence?: number; reasoning?: string } | null) => void;

  // Actions — Linked study
  setLinkedStudyId: (id: string | null) => void;
  setSelectedSiteId: (id: string | null) => void;

  // Actions — UI navigation
  setActiveGroup: (group: string) => void;
  setHighlightTarget: (type: string, id: string) => void;
  clearHighlightTarget: () => void;
  reorderItems: (type: ArtifactType, orderedIds: string[]) => void;
  toggleHistoryPanel: () => void;
  setHistoryPanelOpen: (open: boolean) => void;
  toggleComparisonPanel: () => void;
  setComparisonBuildId: (id: string | null) => void;
}

export const useStudyBuilderStore = create<StudyBuilderState>()((set, get) => ({
  buildId: null,
  messages: [],
  streamingText: "",
  activeToolCalls: [],
  isStreaming: false,
  abortController: null,
  build: null,
  crfs: [],
  visits: [],
  arms: [],
  eligibilityCriteria: [],
  endpoints: [],
  etmfFolders: [],
  etmfDocuments: [],
  queryRules: [],
  studyDocuments: [],
  etmfFilingFile: null,
  etmfFilingSuggestion: null,
  currentRound: 0,
  maxRounds: 40,
  retryStatus: null,
  tokenUsage: null,
  linkedStudyId: null,
  selectedSiteId: null,
  activeGroup: "dashboard",
  selectedArtifact: null,
  expandedTreeNodes: new Set(["arms", "eligibility", "endpoints", "crfs", "visits", "etmf", "documents"]),
  drawerOpen: false,
  editHistory: [],
  highlightTarget: null,
  historyPanelOpen: false,
  comparisonPanelOpen: false,
  comparisonBuildId: null,

  setBuildId: (id) => set({ buildId: id }),

  loadConversation: (buildId, messages) =>
    set({ buildId, messages, streamingText: "", activeToolCalls: [], isStreaming: false }),

  addUserMessage: (content) => {
    const msg: StudyBuilderMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      createdAt: new Date().toISOString(),
    };
    set((state) => ({ messages: [...state.messages, msg] }));
  },

  startStreaming: (controller) =>
    set({ streamingText: "", activeToolCalls: [], isStreaming: true, abortController: controller, currentRound: 0, maxRounds: 40, retryStatus: null, tokenUsage: null }),

  appendStreamingText: (chunk) =>
    set((state) => ({ streamingText: state.streamingText + chunk })),

  addToolCall: (tool, input) =>
    set((state) => ({
      activeToolCalls: [...state.activeToolCalls, { tool, input, status: "running" }],
    })),

  completeToolCall: (tool, summary) =>
    set((state) => ({
      activeToolCalls: state.activeToolCalls.map((tc) =>
        tc.tool === tool && tc.status === "running"
          ? { ...tc, status: "done", summary }
          : tc,
      ),
    })),

  commitAssistantMessage: (id, artifacts, edits, question) => {
    const { streamingText, activeToolCalls } = get();
    const msg: StudyBuilderMessage = {
      id,
      role: "assistant",
      content: streamingText,
      artifactsCreated: artifacts,
      editsApplied: edits,
      question,
      createdAt: new Date().toISOString(),
    };
    set((state) => ({
      messages: [...state.messages, msg],
      streamingText: "",
      activeToolCalls: activeToolCalls.map((tc) =>
        tc.status === "running" ? { ...tc, status: "done" } : tc,
      ),
      isStreaming: false,
      abortController: null,
    }));
  },

  handleStreamError: (error) => {
    const { streamingText } = get();
    const msg: StudyBuilderMessage = {
      id: `error-${Date.now()}`,
      role: "assistant",
      content: streamingText
        ? `${streamingText}\n\n*Error: ${error}*`
        : `*Error: ${error}*`,
      createdAt: new Date().toISOString(),
    };
    set((state) => ({
      messages: [...state.messages, msg],
      streamingText: "",
      activeToolCalls: [],
      isStreaming: false,
      abortController: null,
    }));
  },

  cancelStream: () => {
    const { abortController } = get();
    abortController?.abort();
    set({ isStreaming: false, abortController: null });
  },

  setRoundInfo: (round, maxRounds) =>
    set({ currentRound: round, maxRounds, retryStatus: null }),

  setRetryStatus: (status) =>
    set({ retryStatus: status }),

  setTokenUsage: (inputTokens, outputTokens) =>
    set({ tokenUsage: { inputTokens, outputTokens } }),

  loadBuild: (data) =>
    set({
      build: data.build,
      crfs: data.crfs,
      visits: data.visits,
      arms: data.arms,
      eligibilityCriteria: data.eligibilityCriteria,
      endpoints: data.endpoints,
      etmfFolders: data.etmfFolders,
      etmfDocuments: data.etmfDocuments ?? [],
      queryRules: data.queryRules,
      studyDocuments: data.studyDocuments ?? [],
      selectedArtifact: null,
    }),

  selectArtifact: (type, id) => set({ selectedArtifact: { type, id }, drawerOpen: true }),

  toggleTreeNode: (nodeId) =>
    set((state) => {
      const next = new Set(state.expandedTreeNodes);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return { expandedTreeNodes: next };
    }),

  setDrawerOpen: (open) => set({ drawerOpen: open }),

  applyEdit: (edit) =>
    set((state) => {
      const newState: Partial<StudyBuilderState> = {
        editHistory: [...state.editHistory, edit],
      };

      if (edit.artifactType === "crf") {
        newState.crfs = state.crfs.map((crf) => {
          if (crf.id !== edit.artifactId) return crf;
          return applyFieldPath(crf, edit.fieldPath, edit.newValue) as CrfDefinition;
        });
      } else if (edit.artifactType === "visit") {
        newState.visits = state.visits.map((v) => {
          if (v.id !== edit.artifactId) return v;
          return applyFieldPath(v, edit.fieldPath, edit.newValue) as VisitDefinition;
        });
      } else if (edit.artifactType === "arm") {
        newState.arms = state.arms.map((a) => {
          if (a.id !== edit.artifactId) return a;
          return applyFieldPath(a, edit.fieldPath, edit.newValue) as StudyArm;
        });
      }

      return newState;
    }),

  clearBuilder: () =>
    set({
      buildId: null,
      messages: [],
      streamingText: "",
      activeToolCalls: [],
      isStreaming: false,
      abortController: null,
      currentRound: 0,
      maxRounds: 40,
      retryStatus: null,
      tokenUsage: null,
      build: null,
      crfs: [],
      visits: [],
      arms: [],
      eligibilityCriteria: [],
      endpoints: [],
      etmfFolders: [],
      etmfDocuments: [],
      queryRules: [],
      studyDocuments: [],
      etmfFilingFile: null,
      etmfFilingSuggestion: null,
      linkedStudyId: null,
      selectedSiteId: null,
      activeGroup: "dashboard",
      selectedArtifact: null,
      drawerOpen: false,
      editHistory: [],
      historyPanelOpen: false,
      comparisonPanelOpen: false,
      comparisonBuildId: null,
    }),

  // ── Incremental artifact updates ──────────────────────

  addCrf: (crf) =>
    set((state) => ({
      crfs: state.crfs.some((c) => c.id === crf.id) ? state.crfs : [...state.crfs, crf],
    })),

  addVisit: (visit) =>
    set((state) => ({
      visits: state.visits.some((v) => v.id === visit.id) ? state.visits : [...state.visits, visit],
    })),

  addArm: (arm) =>
    set((state) => ({
      arms: state.arms.some((a) => a.id === arm.id) ? state.arms : [...state.arms, arm],
    })),

  addEligibilityCriteria: (criteria) =>
    set((state) => {
      const existingIds = new Set(state.eligibilityCriteria.map((c) => c.id));
      const newCriteria = criteria.filter((c) => !existingIds.has(c.id));
      return newCriteria.length > 0
        ? { eligibilityCriteria: [...state.eligibilityCriteria, ...newCriteria] }
        : {};
    }),

  addEndpoint: (endpoint) =>
    set((state) => ({
      endpoints: state.endpoints.some((e) => e.id === endpoint.id) ? state.endpoints : [...state.endpoints, endpoint],
    })),

  addEtmfFolder: (folder) =>
    set((state) => ({
      etmfFolders: state.etmfFolders.some((f) => f.id === folder.id) ? state.etmfFolders : [...state.etmfFolders, folder],
    })),

  addEtmfDocument: (doc) =>
    set((state) => ({
      etmfDocuments: state.etmfDocuments.some((d) => d.id === doc.id) ? state.etmfDocuments : [...state.etmfDocuments, doc],
    })),

  addQueryRule: (rule) =>
    set((state) => ({
      queryRules: state.queryRules.some((r) => r.id === rule.id) ? state.queryRules : [...state.queryRules, rule],
    })),

  addStudyDocument: (doc) =>
    set((state) => ({
      studyDocuments: state.studyDocuments.some((d) => d.id === doc.id) ? state.studyDocuments : [...state.studyDocuments, doc],
    })),

  updateOverview: (partial) =>
    set((state) => ({
      build: state.build ? { ...state.build, ...partial } : null,
    })),

  setEtmfFilingFile: (file) => set({ etmfFilingFile: file }),
  setEtmfFilingSuggestion: (s) => set({ etmfFilingSuggestion: s }),

  // ── Linked study ─────────────────────────────────────

  setLinkedStudyId: (id) => set({ linkedStudyId: id }),

  setSelectedSiteId: (id) => set({ selectedSiteId: id }),

  // ── UI navigation ──────────────────────────────────────

  setActiveGroup: (group) =>
    set({ activeGroup: group, selectedArtifact: null, highlightTarget: null }),

  setHighlightTarget: (type, id) => set({ highlightTarget: { type, id } }),

  clearHighlightTarget: () => set({ highlightTarget: null }),

  toggleHistoryPanel: () =>
    set((state) => ({
      historyPanelOpen: !state.historyPanelOpen,
      comparisonPanelOpen: state.historyPanelOpen ? state.comparisonPanelOpen : false,
    })),

  setHistoryPanelOpen: (open) => set({ historyPanelOpen: open }),

  toggleComparisonPanel: () =>
    set((state) => ({
      comparisonPanelOpen: !state.comparisonPanelOpen,
      historyPanelOpen: state.comparisonPanelOpen ? state.historyPanelOpen : false,
    })),

  setComparisonBuildId: (id) => set({ comparisonBuildId: id }),

  reorderItems: (type, orderedIds) =>
    set((state) => {
      switch (type) {
        case "crf": {
          const map = new Map(state.crfs.map((c) => [c.id, c]));
          return { crfs: orderedIds.map((id) => map.get(id)!).filter(Boolean) };
        }
        case "visit": {
          const map = new Map(state.visits.map((v) => [v.id, v]));
          return { visits: orderedIds.map((id) => map.get(id)!).filter(Boolean) };
        }
        case "arm": {
          const map = new Map(state.arms.map((a) => [a.id, a]));
          return { arms: orderedIds.map((id) => map.get(id)!).filter(Boolean) };
        }
        case "eligibility": {
          const map = new Map(state.eligibilityCriteria.map((e) => [e.id, e]));
          return { eligibilityCriteria: orderedIds.map((id) => map.get(id)!).filter(Boolean) };
        }
        case "endpoint": {
          const map = new Map(state.endpoints.map((e) => [e.id, e]));
          return { endpoints: orderedIds.map((id) => map.get(id)!).filter(Boolean) };
        }
        case "etmf": {
          const map = new Map(state.etmfFolders.map((f) => [f.id, f]));
          return { etmfFolders: orderedIds.map((id) => map.get(id)!).filter(Boolean) };
        }
        default:
          return {};
      }
    }),
}));

// Convenience selector — production builds are read-only
export const selectIsReadOnly = (s: StudyBuilderState) => s.build?.environment === "production";

function applyFieldPath(obj: unknown, path: string, value: unknown): unknown {
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  if (parts.length === 0) return value;

  const clone = Array.isArray(obj) ? [...obj] : { ...(obj as Record<string, unknown>) };
  const key = parts[0];
  if (parts.length === 1) {
    (clone as Record<string, unknown>)[key] = value;
  } else {
    (clone as Record<string, unknown>)[key] = applyFieldPath(
      (clone as Record<string, unknown>)[key],
      parts.slice(1).join("."),
      value,
    );
  }
  return clone;
}
